import * as React from 'react';
import { useState } from 'react';
import * as HoverCardPrimitive from '@radix-ui/react-hover-card';
import { cn } from '@/utils/cn';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { BottomSheet } from './BottomSheet';

/**
 * HoverCard Component
 *
 * A Radix UI-based hover card for displaying rich content on hover.
 * Automatically handles positioning and collision detection.
 *
 * @example
 * <HoverCard
 *   trigger={<button>Hover me</button>}
 *   side="left"
 *   align="start"
 * >
 *   <div className="p-4">Rich content here</div>
 * </HoverCard>
 */

/** Hover popover, not a modal: a fixed tier above the whole modal stack range (80..130), below toasts (150). */
const HOVER_CARD_Z = 140;

export interface HoverCardProps {
  /** The element that triggers the hover card */
  trigger: React.ReactNode;
  /** The content to display in the hover card */
  children: React.ReactNode;
  /** Which side to show the card */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Alignment relative to trigger */
  align?: 'start' | 'center' | 'end';
  /** Distance from trigger in pixels */
  sideOffset?: number;
  /** Delay before opening (ms) */
  openDelay?: number;
  /** Delay before closing (ms) */
  closeDelay?: number;
  /** Additional classes for the card */
  className?: string;
  /** Whether the card is open (controlled) */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Mobile animation style: 'slide' (default) or 'fade' */
  mobileAnimation?: 'slide' | 'fade';
  /** Allow pointer events on trigger (needed for drag-and-drop). Default: false */
  allowTriggerPointerEvents?: boolean;
  /** Show the mobile sheet while preserving the trigger's pointer events for drag-and-drop. */
  mobileSheetWithTriggerEvents?: boolean;
  /**
   * Extra padding (px) from each viewport edge before the collision-flip kicks in.
   * Accepts a uniform number or per-side object.
   * Default: { top: 72, right: 16, bottom: 16, left: 16 }
   * The larger top value accounts for the fixed DashboardNavbar (≈65.6 px) so that
   * tooltips with side="top" on triggers near the top of the page correctly flip to
   * side="bottom" rather than rendering under the navbar.
   */
  collisionPadding?: number | Partial<Record<'top' | 'right' | 'bottom' | 'left', number>>;
}

// Default collision padding: large top value so the card flips away from the
// fixed DashboardNavbar (≈65.6 px) instead of sliding under it.
const DEFAULT_COLLISION_PADDING = { top: 72, right: 16, bottom: 16, left: 16 } as const;

export function HoverCard({
  trigger,
  children,
  side = 'left',
  align = 'start',
  sideOffset = 8,
  openDelay = 100,
  closeDelay = 100,
  className,
  open,
  onOpenChange,
  mobileAnimation = 'slide',
  allowTriggerPointerEvents = false,
  mobileSheetWithTriggerEvents = false,
  collisionPadding = DEFAULT_COLLISION_PADDING,
}: HoverCardProps) {
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const [mobileOpen, setMobileOpen] = useState(false);

  const isOpen = open ?? mobileOpen;
  const setIsOpen = onOpenChange ?? setMobileOpen;

  const handleDesktopOpenChange = (next: boolean) => {
    onOpenChange?.(next);
  };

  if (isMobile) {
    // Draggable items: skip BottomSheet wrapper, render trigger directly
    if (allowTriggerPointerEvents && !mobileSheetWithTriggerEvents) {
      return <>{trigger}</>;
    }

    return (
      <BottomSheet
        open={isOpen}
        onOpenChange={setIsOpen}
        trigger={trigger}
        className={className}
        animation={mobileAnimation}
        allowTriggerPointerEvents={mobileSheetWithTriggerEvents}
      >
        {children}
      </BottomSheet>
    );
  }

  return (
    <HoverCardPrimitive.Root
      openDelay={openDelay}
      closeDelay={closeDelay}
      open={open}
      onOpenChange={handleDesktopOpenChange}
    >
      <HoverCardPrimitive.Trigger asChild>
        {trigger}
      </HoverCardPrimitive.Trigger>
      {/*
       * Portal renders the content into document.body, escaping any
       * overflow:hidden / overflow:auto / stacking-context ancestors in the
       * component tree (e.g. the overflow-auto scroll container on the
       * Portfolio page).  avoidCollisions + collisionPadding then ensure the
       * card flips away from viewport edges — including the fixed navbar.
       */}
      <HoverCardPrimitive.Portal>
        <HoverCardPrimitive.Content
          style={{ zIndex: HOVER_CARD_Z }}
          className={cn(
            'w-72 rounded-2xl bg-white shadow-2xl border border-gray-100 overflow-hidden',
            'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            className
          )}
          side={side}
          align={align}
          sideOffset={sideOffset}
          avoidCollisions={true}
          collisionPadding={collisionPadding}
          sticky="partial"
        >
          {children}
        </HoverCardPrimitive.Content>
      </HoverCardPrimitive.Portal>
    </HoverCardPrimitive.Root>
  );
}

// Re-export primitives for advanced use cases
export const HoverCardRoot = HoverCardPrimitive.Root;
export const HoverCardTrigger = HoverCardPrimitive.Trigger;
export const HoverCardContent = HoverCardPrimitive.Content;
export const HoverCardPortal = HoverCardPrimitive.Portal;
